import React, { FC, useEffect } from 'react';

import type { IdentityTypeEnum } from '@/gql/types.generated';
//eslint-disable-next-line no-restricted-imports
import { useHistory, useParams, Link } from 'react-router-dom';
import { Button, Form, Input } from 'antd';
import { useSignInRequest, SignInMutation } from './singin.generated';
import capitalize from 'lodash/capitalize';
import { usePrefetchPartnerData } from '@/shared/access';
import { LocalStorage } from '@/core/Storage';
import { getAccountfromUrl } from '@/core/getAccountfromUrl';
import { LoginLayout } from '@pages/Login/LoginLayout';
import styled from 'styled-components';
import { emailValidator } from '@/libs/validators';
import { gettext, useGettext } from '@cranium/i18n';
import { Register, Title } from '@pages/Login/component';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { OauthProviderEnum } from '@/gql/types.generated';

function requiredValidator(type: 'email' | 'phone') {
  return (rule: any, value: string, callBack: (message?: any) => void) => {
    callBack(!value ? (type === 'email' ? gettext('Email is incorrect') : gettext('Phone is incorrect')) : undefined);
  };
}

const SUPPORTED_IDENTITY_TYPES = (process.env.REACT_APP_SUPPORTED_IDENTITY_TYPES || '').split(',');
const only1Identity = SUPPORTED_IDENTITY_TYPES.length <= 1;

export const Login: FC = () => {
  const prefetchPartnerData = usePrefetchPartnerData();
  const signin = useSignInRequest();
  const { gettext } = useGettext();
  const { identity } = useParams<{ identity: IdentityTypeEnum }>();
  const history = useHistory();
  const [form] = Form.useForm<{ email: string; password: string }>();

  useEffect(() => {
    // @ts-ignore
    const isSupportedIdentity = ['contractor', 'driver', 'supplier', 'partner', 'super_admin'].includes(identity);
    !isSupportedIdentity && history.push('/login/');
  }, [identity]);

  const handleLogin = (data?: SignInMutation) => {
    const type = identity;
    const partnerid = data?.authSignIn?.partner?.id;
    if (partnerid) {
      const prev_partner = getAccountfromUrl(LocalStorage.getItem<string>('location.beforeLogin') || '');
      if (prev_partner) {
        return prefetchPartnerData(prev_partner, type, data)
          .then(() => {
            const locationBeforeLogin = LocalStorage.getItem<string>('location.beforeLogin');
            LocalStorage.removeItem('location.beforeLogin');
            history.push(
              locationBeforeLogin && !locationBeforeLogin.includes('login') ? locationBeforeLogin : `/p-${prev_partner}/`
            );
          })
          .catch(err => {
            return prefetchPartnerData(partnerid, type, data);
          })
          .then(() => {
            LocalStorage.removeItem('location.beforeLogin');
            history.push(`/p-${partnerid}/`);
          });
      }
      return prefetchPartnerData(partnerid, type, data).then(() => {
        LocalStorage.removeItem('location.beforeLogin');
        history.push(`/p-${partnerid}/`);
      });
    }
  };
  const handleSubmit = async () => {
    const values = await form.validateFields();
    const type = identity;
    LocalStorage.setItem('identityType', type);
    //sign-in
    return signin({
      email: { email: values.email, password: values.password },
      type,
      isPartner: type === 'partner'
    }).then(resp => {
      handleLogin(resp.data);
    });
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <LoginLayout>
        <Main>
          <Header>
            <Title>{gettext('Welcome!')}</Title>
            <Register />
          </Header>
          {only1Identity ? null : (
            <Row>
              <IdentityType>{gettext('Login as a %(identity)', { identity: capitalize(identity) })}</IdentityType>
              <Link to={'/login'}>{gettext('(Change)')}</Link>
            </Row>
          )}
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label={gettext('Email')}
              name="email"
              rules={[{ validator: emailValidator }, { validator: requiredValidator('email') }]}
            >
              <Input type="email" placeholder={gettext('Email')} />
            </Form.Item>
            <Form.Item label={gettext('Password')} name="password" rules={[{ required: true, message: gettext('Required') }]}>
              <Input type="password" placeholder={gettext('Password')} />
            </Form.Item>
            <Link to={`/forgot-password/${identity}`}>{gettext('Forgot password?')}</Link>
            <Button type="primary" onClick={() => handleSubmit()}>
              {gettext('Login')}
            </Button>
            {process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
              <GoogleLogin
                onSuccess={credentialResponse => {
                  if (!credentialResponse?.credential) {
                    return;
                  }
                  const type = identity;
                  LocalStorage.setItem('identityType', type);
                  //sign-in
                  return signin({
                    type,
                    isPartner: type === 'partner',
                    oauth: {
                      provider: OauthProviderEnum.Google,
                      token: credentialResponse.credential
                    }
                  }).then(resp => {
                    handleLogin(resp.data);
                  });
                }}
                onError={() => {
                  console.log('Login Failed');
                }}
              />
            ) : null}
          </Form>
        </Main>
      </LoginLayout>
    </GoogleOAuthProvider>
  );
};

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  > h1 {
    margin-bottom: 0;
  }
  padding-bottom: 12px;
`;

const IdentityType = styled.div`
  color: var(--gray-10);
  font: var(--font-regular-16);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-bottom: 8px;
`;

const Main = styled.div`
  width: 420px;
  max-width: 100%;

  > form {
    display: flex;
    flex-direction: column;
  }
  button {
    align-self: stretch;
    margin-bottom: 24px;
  }
  a {
    display: flex;
    align-items: center;
    gap: 4px;
    &:hover {
      color: var(--green-7);
      text-decoration: none;
    }
  }
`;
